import React, { useRef, useState } from 'react'
import {
  Animated,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View
} from 'react-native'
import { Feather } from '@expo/vector-icons'

import { CORES, sombraCard } from '../../../template/Etapas/styles'
import { corComAlpha, corTextoSobre, gradienteHero } from '../../../theme/cores'

// Layout novo da área do cliente. O layout antigo (Index.tsx) continua sendo
// usado pelo modo legado (pwa simples / layout v2 / tema escuro).

export interface BotaoMenuNovo {
  label: string
  subtitulo: string
  iconeNovo: string
  onPress: () => void
}

interface Props {
  corPrincipal: string
  nomeCliente: string
  nomeEmpresa: string
  estatisticas: { agendamentosRealizados: number; pacotesUtilizados: number }
  carregandoEstatisticas: boolean
  botoes: BotaoMenuNovo[]
  onNovoAgendamento: () => void
  onMenuConta: (id: number) => void // 1 voltar ao início, 2 sair
}

const DOURADO = '#E3BF7A'
const VERMELHO = '#C0392B'

function iniciais(nome: string) {
  const partes = String(nome || '')
    .trim()
    .split(' ')
    .filter((p) => p)
  if (partes.length === 0) return '?'
  const ultima = partes.length > 1 ? partes[partes.length - 1].charAt(0) : ''
  return (partes[0].charAt(0) + ultima).toUpperCase()
}

function circulo(
  tamanho: number,
  top: number,
  right: number
): React.CSSProperties {
  return {
    position: 'absolute',
    width: tamanho,
    height: tamanho,
    top,
    right,
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.12)',
    pointerEvents: 'none'
  }
}

export default function MenuNovo({
  corPrincipal,
  nomeCliente,
  nomeEmpresa,
  estatisticas,
  carregandoEstatisticas,
  botoes,
  onNovoAgendamento,
  onMenuConta
}: Props) {
  const corTexto = corTextoSobre(corPrincipal)
  const primeiroNome = String(nomeCliente || '').trim().split(' ')[0]

  const [menuAberto, setMenuAberto] = useState(false)
  const animacao = useRef(new Animated.Value(0)).current

  function abrirMenu() {
    setMenuAberto(true)
    Animated.timing(animacao, {
      toValue: 1,
      duration: 250,
      useNativeDriver: true
    }).start()
  }

  function fecharMenu(depois?: () => void) {
    Animated.timing(animacao, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true
    }).start(() => {
      setMenuAberto(false)
      depois?.()
    })
  }

  const translateY = animacao.interpolate({
    inputRange: [0, 1],
    outputRange: [320, 0]
  })

  function renderAvatar(tamanho: number, fundo: string, corLetra: string) {
    return (
      <View
        style={[
          styles.avatar,
          {
            width: tamanho,
            height: tamanho,
            borderRadius: tamanho / 2,
            backgroundColor: fundo
          }
        ]}
      >
        <Text
          style={[
            styles.textoAvatar,
            { color: corLetra, fontSize: tamanho * 0.34 }
          ]}
        >
          {iniciais(nomeCliente)}
        </Text>
      </View>
    )
  }

  function renderEstatistica(valor: number, rotulo: string) {
    return (
      <View style={{ flex: 1 }}>
        <Text style={styles.numeroEstatistica}>
          {carregandoEstatisticas ? '...' : valor}
        </Text>
        <Text style={styles.rotuloEstatistica}>{rotulo}</Text>
      </View>
    )
  }

  function renderBotao(botao: BotaoMenuNovo) {
    return (
      <TouchableOpacity
        key={botao.label}
        activeOpacity={0.85}
        onPress={botao.onPress}
        style={[styles.cardAtalho, sombraCard]}
      >
        <View style={styles.linhaTopoAtalho}>
          <View
            style={[
              styles.iconeAtalho,
              { backgroundColor: corComAlpha(corPrincipal, 0.1) }
            ]}
          >
            <Feather
              name={botao.iconeNovo as any}
              size={18}
              color={corPrincipal}
            />
          </View>
          <Feather name='arrow-up-right' size={15} color='#c2bcb2' />
        </View>

        <Text style={styles.tituloAtalho}>{botao.label}</Text>
        <Text style={styles.subtituloAtalho}>{botao.subtitulo}</Text>
      </TouchableOpacity>
    )
  }

  function renderItemMenuConta(params: {
    icone: string
    titulo: string
    subtitulo: string
    perigo?: boolean
    onPress: () => void
  }) {
    const cor = params.perigo ? VERMELHO : CORES.titulo

    return (
      <TouchableOpacity style={styles.itemConta} onPress={params.onPress}>
        <View
          style={[
            styles.iconeConta,
            {
              backgroundColor: params.perigo
                ? 'rgba(192,57,43,0.1)'
                : 'rgba(0,0,0,0.05)'
            }
          ]}
        >
          <Feather name={params.icone as any} size={18} color={cor} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={[styles.tituloItemConta, { color: cor }]}>
            {params.titulo}
          </Text>
          <Text style={styles.subtituloItemConta}>{params.subtitulo}</Text>
        </View>
        <Feather name='chevron-right' size={18} color='#c2bcb2' />
      </TouchableOpacity>
    )
  }

  return (
    <View style={styles.container}>
      <ScrollView
        style={{ flex: 1, width: '100%' }}
        contentContainerStyle={{ alignItems: 'center', paddingBottom: 90 }}
      >
        {/* Topo */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box',
            paddingTop: 22,
            paddingBottom: 76,
            paddingLeft: 20,
            paddingRight: 20,
            backgroundColor: corPrincipal,
            backgroundImage: gradienteHero(corPrincipal),
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8
          }}
        >
          <div style={circulo(220, -70, -70)} />
          <div style={circulo(140, -30, -30)} />

          <View style={styles.largura}>
            <View style={styles.linhaTopo}>
              <Text style={styles.rotuloArea}>ÁREA DO CLIENTE</Text>
              <TouchableOpacity style={styles.botaoMenu} onPress={abrirMenu}>
                <Feather name='menu' size={18} color={corTexto} />
              </TouchableOpacity>
            </View>

            <View style={styles.linhaSaudacao}>
              {renderAvatar(56, '#fff', corPrincipal)}
              <View style={{ flex: 1 }}>
                <Text
                  style={[styles.saudacao, { color: corTexto }]}
                  numberOfLines={1}
                >
                  Olá, {primeiroNome}
                </Text>
                <Text style={[styles.subSaudacao, { color: corTexto }]}>
                  Bom te ver de novo por aqui.
                </Text>
              </View>
            </View>
          </View>
        </div>

        <View style={styles.largura}>
          {/* Resumo */}
          <View style={[styles.cardResumo, sombraCard]}>
            {renderEstatistica(
              estatisticas.agendamentosRealizados,
              'Agendamentos\nrealizados'
            )}
            <View style={styles.separador} />
            {renderEstatistica(
              estatisticas.pacotesUtilizados,
              'Pacotes\nutilizados'
            )}
          </View>

          <Text style={styles.rotuloSecao}>SUA CONTA</Text>

          <View style={styles.grade}>
            {botoes.map((botao) => renderBotao(botao))}
          </View>

          <TouchableOpacity
            activeOpacity={0.85}
            onPress={onNovoAgendamento}
            style={[styles.botaoNovo, { backgroundColor: corPrincipal }]}
          >
            <Feather name='calendar' size={18} color={corTexto} />
            <Text style={[styles.textoBotaoNovo, { color: corTexto }]}>
              Novo agendamento
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Menu da conta (bottom sheet) */}
      <Modal
        visible={menuAberto}
        transparent
        animationType='none'
        onRequestClose={() => fecharMenu()}
      >
        <Pressable style={styles.overlay} onPress={() => fecharMenu()}>
          <Animated.View
            style={[styles.folha, { transform: [{ translateY }] }]}
          >
            <Pressable>
              <View style={styles.alca} />

              <View style={styles.cabecalhoConta}>
                {renderAvatar(44, corComAlpha(corPrincipal, 0.1), corPrincipal)}
                <View style={{ flex: 1 }}>
                  <Text style={styles.nomeConta}>{nomeCliente}</Text>
                  {!!nomeEmpresa && (
                    <Text style={styles.empresaConta}>
                      Conectado na {nomeEmpresa}
                    </Text>
                  )}
                </View>
              </View>

              {renderItemMenuConta({
                icone: 'home',
                titulo: 'Voltar ao início',
                subtitulo: 'Página da barbearia',
                onPress: () => fecharMenu(() => onMenuConta(1))
              })}

              <View style={styles.divisorConta} />

              {renderItemMenuConta({
                icone: 'log-out',
                titulo: 'Sair da conta',
                subtitulo: 'Você precisará entrar de novo',
                perigo: true,
                onPress: () => fecharMenu(() => onMenuConta(2))
              })}
            </Pressable>
          </Animated.View>
        </Pressable>
      </Modal>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    backgroundColor: CORES.fundo
  },
  largura: {
    width: '100%',
    maxWidth: 560,
    paddingHorizontal: 16,
    alignSelf: 'center'
  },
  linhaTopo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  rotuloArea: {
    color: DOURADO,
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 2
  },
  botaoMenu: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.14)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center',
    justifyContent: 'center'
  },
  linhaSaudacao: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginTop: 22
  },
  avatar: {
    alignItems: 'center',
    justifyContent: 'center'
  },
  textoAvatar: {
    fontWeight: '800'
  },
  saudacao: {
    fontSize: 26,
    fontWeight: '800'
  },
  subSaudacao: {
    fontSize: 14,
    opacity: 0.85,
    marginTop: 2
  },
  cardResumo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: -50,
    padding: 18,
    borderRadius: 20,
    backgroundColor: CORES.card,
    borderWidth: 1,
    borderColor: CORES.borda
  },
  separador: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: CORES.borda,
    marginHorizontal: 16
  },
  numeroEstatistica: {
    fontSize: 30,
    fontWeight: '800',
    color: CORES.titulo
  },
  rotuloEstatistica: {
    fontSize: 12,
    color: CORES.subtitulo,
    marginTop: 2
  },
  rotuloSecao: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.5,
    color: CORES.subtitulo,
    marginTop: 24,
    marginBottom: 12
  },
  grade: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: 12
  },
  cardAtalho: {
    width: '48.5%',
    minHeight: 120,
    padding: 14,
    borderRadius: 18,
    backgroundColor: CORES.card,
    borderWidth: 1,
    borderColor: CORES.borda
  },
  linhaTopoAtalho: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 14
  },
  iconeAtalho: {
    width: 38,
    height: 38,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center'
  },
  tituloAtalho: {
    fontSize: 16,
    fontWeight: '800',
    color: CORES.titulo
  },
  subtituloAtalho: {
    fontSize: 12,
    color: CORES.subtitulo,
    marginTop: 3
  },
  botaoNovo: {
    height: 52,
    borderRadius: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 16
  },
  textoBotaoNovo: {
    fontSize: 16,
    fontWeight: '700'
  },
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.4)'
  },
  folha: {
    width: '100%',
    maxWidth: 560,
    backgroundColor: CORES.card,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 28
  },
  alca: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(0,0,0,0.12)',
    marginBottom: 18
  },
  cabecalhoConta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingBottom: 16,
    marginBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: CORES.borda
  },
  nomeConta: {
    fontSize: 16,
    fontWeight: '700',
    color: CORES.titulo
  },
  empresaConta: {
    fontSize: 12,
    color: CORES.subtitulo,
    marginTop: 2
  },
  itemConta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 14
  },
  iconeConta: {
    width: 40,
    height: 40,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center'
  },
  tituloItemConta: {
    fontSize: 15,
    fontWeight: '700'
  },
  subtituloItemConta: {
    fontSize: 12,
    color: CORES.subtitulo,
    marginTop: 2
  },
  divisorConta: {
    height: 1,
    backgroundColor: CORES.borda
  }
})
